import { Formik, Form, useFormikContext } from "formik";
import * as React from "react";

import type { FormModel, RouteLink } from "../types/FormTypes";
import type {
  FindGroupJourneysParams,
  JourneyApiResult,
} from "../types/JourneyTypes";

import { findGroupJourneys } from "../api/journeys";
import { findPoints } from "../api/points";
import type { Stop } from "../api/points";

import { useAtom } from "jotai";
import * as Yup from "yup";
import { formDataAtom, travelSearchAtom, selectedJourneyAtom } from "../App";
import { useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import dayjs from "../utils/dayjsConfig";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import ScreenReaderAnnouncer, {
  getJourneySrLabel,
} from "../components/ScreenReaderAnnouncer";

import {
  ErrorList,
  Progressbar,
  Box,
  CustomAutocomplete,
  DatePicker,
  RadioGroupForm,
  Button,
  Label,
  Input,
  Icon,
  Alert,
  AlertTitle,
  Badge,
  cn,
  TrafficBadge,
  Heading,
} from "@ogt/ui";

export const ROUND_TRIP = "round_trip";
export const ONEWAY_TRIP = "one_way_trip";

type Journeys = JourneyApiResult;

type TrafficMeta = Parameters<typeof TrafficBadge>[0]["meta"];

function toTrafficMeta(link: RouteLink): TrafficMeta {
  return {
    iconCss: link.Line.Ogt.OgtTrafficTypeIconCssClass,
    groupCss: link.Line.Ogt.OgtTrafficTypeGroupCssClass,
    title: link.Line.Ogt.OgtLineNamePreformatted ?? link.Line.LineName,
  };
}

interface JourneySelectionWatcherProps {
  searchResults: Journeys[];
  onSelect: (value: string, result: Journeys) => void;
}

function JourneySelectionWatcher({
  searchResults,
  onSelect,
}: JourneySelectionWatcherProps) {
  const { values } = useFormikContext<FormModel>();
  const handledJourneyKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const key = values.journey;

    if (!key) {
      handledJourneyKeyRef.current = null;
      return;
    }

    if (handledJourneyKeyRef.current === key) {
      return;
    }

    const selectedResult = searchResults.find(
      (journey) => journey.JourneyKey === key
    );

    if (!selectedResult) {
      return;
    }

    handledJourneyKeyRef.current = key;
    onSelect(key, selectedResult);
  }, [values.journey, searchResults, onSelect]);

  return null;
}

export default function TravelSearch() {
  //const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const progress = 16;

  const [formData, setFormData] = useAtom(formDataAtom);
  const [travelSearch, setTravelSearch] = useAtom(travelSearchAtom);
  const [selectedJourney, setSelectedJourney] = useAtom(selectedJourneyAtom);

  const [searchErrors, setSearchErrors] = useState({
    journeyFromError: "",
    journeyToError: "",
    journeyTimeError: "",
    journeyDateError: "",
    journeyFormError: "",
    formError: "",
  });

  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [fromQuery, setFromQuery] = useState<string>("");
  const [toQuery, setToQuery] = useState<string>("");
  const [fromResults, setFromResults] = useState<Stop[]>([]);
  const [toResults, setToResults] = useState<Stop[]>([]);
  const [searchResults, setSearchResults] = useState<Journeys[]>([]);
  const [searchType, setSearchType] = useState<"from" | "to" | null>(null);
  const [debouncedFromQuery] = useDebounce(fromQuery, 300);
  const [debouncedToQuery] = useDebounce(toQuery, 300);
  const [autocompleteAnnouncement, setAutocompleteAnnouncement] = useState("");

  useEffect(() => {
    if (!searchType) return;

    const results = searchType === "from" ? fromResults : toResults;

    if (!results) return;

    if (results.length > 0) {
      setAutocompleteAnnouncement(
        `Det finns ${results.length} sökförslag. Använd upp och nedpilar för att navigera.`
      );
    } else {
      setAutocompleteAnnouncement("");
    }
  }, [fromResults, toResults, searchType]);

  useEffect(() => {
    if (
      travelSearch.selectedJourneyFrom &&
      travelSearch.selectedJourneyTo &&
      travelSearch.journeyDate &&
      travelSearch.journeyTime
    ) {
      doTravelSearch(false, false);

      setFromQuery(travelSearch.selectedJourneyFrom.PlaceName);
      setToQuery(travelSearch.selectedJourneyTo.PlaceName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchType) return;

    const query = searchType === "from" ? debouncedFromQuery : debouncedToQuery;

    const trimmed = (query ?? "").trim();

    if (trimmed.length < 2) {
      if (searchType === "from") {
        setFromResults([]);
      } else {
        setToResults([]);
      }

      return;
    }

    const controller = new AbortController();

    void findPoints(trimmed, controller.signal)
      .then((results) => {
        if (searchType === "from") {
          setFromResults(results);
        } else {
          setToResults(results);
        }
      })
      .catch((error) => {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        console.error("[Points search]", error);

        if (searchType === "from") {
          setFromResults([]);
        } else {
          setToResults([]);
        }
      });

    return () => {
      controller.abort();
    };
  }, [debouncedFromQuery, debouncedToQuery, searchType]);

  const searchFormRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleFocus = () => {
      if (searchFormRef.current) {
        const isActive = searchFormRef.current.contains(document.activeElement);
        setIsFocused(isActive);
      }
    };
    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleFocus);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleFocus);
    };
  }, []);

  const handleSelect = (placeName: string, type: "from" | "to") => {
    const list = type === "from" ? fromResults : toResults;
    const safeList: Stop[] = Array.isArray(list) ? list : [];

    const selectedItem =
      safeList.find((item) => item.PlaceName === placeName) || null;

    if (type === "from") {
      setTravelSearch((prev) => ({
        ...prev,
        selectedJourneyFrom: selectedItem,
      }));

      setFromQuery(selectedItem?.PlaceName ?? "");
      setFromResults([]);
    } else {
      setTravelSearch((prev) => ({
        ...prev,
        selectedJourneyTo: selectedItem,
      }));

      setToQuery(selectedItem?.PlaceName ?? "");
      setToResults([]);
    }

    setSearchType(null);
  };

  const handleQueryChange = (value: string, type: "from" | "to") => {
    if (type === "from") {
      setFromQuery(value);
      if (value.trim() === "") {
        setTravelSearch((p) => ({ ...p, selectedJourneyFrom: null }));
        setFromResults([]);
      }
    } else {
      setToQuery(value);
      if (value.trim() === "") {
        setTravelSearch((p) => ({ ...p, selectedJourneyTo: null }));
        setToResults([]);
      }
    }
    setSearchType(type);
  };

  const handleTravelSearchChange = (
    field: keyof typeof travelSearch,
    value: string
  ) => {
    setTravelSearch((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      doTravelSearch(true);
    }
  };

  const formatTimeInput = (value: string) => {
    const cleanedInput = value.replace(/\D/g, "");
    let newTime = "00:00";

    if (cleanedInput.length === 1) {
      newTime = `0${cleanedInput}:00`;
    } else if (cleanedInput.length === 2) {
      const hours = cleanedInput;
      newTime = `${hours === "24" ? "00" : hours}:00`;
    } else if (cleanedInput.length === 3) {
      const hours =
        cleanedInput[0] === "2" && cleanedInput[1] === "4"
          ? "00"
          : `0${cleanedInput[0]}`;
      const minutes = cleanedInput.slice(1);
      newTime = `${hours}:${minutes}`;
    } else if (cleanedInput.length >= 4) {
      let hours = cleanedInput.slice(0, 2);
      const minutes = cleanedInput.slice(2, 4);
      if (hours === "24") {
        hours = "00";
      }
      newTime = `${hours}:${minutes}`;
    }

    setTravelSearch((prev) => ({
      ...prev,
      journeyTime: newTime,
    }));

    return newTime;
  };
  const doTravelSearch = async (
    clearSelectedJourney: boolean = false,
    announce: boolean = true
  ) => {
    if (announce) {
      setHasSearched(true);
      setIsSearching(true);
    }
    const errors = {
      journeyFromError: "",
      journeyToError: "",
      journeyDateError: "",
      journeyTimeError: "",
    };

    if (
      !travelSearch.selectedJourneyFrom?.PlaceName.length ||
      travelSearch.selectedJourneyFrom.PlaceName.length < 1
    ) {
      errors.journeyFromError = "Vänligen fyll i en avreseplats";
    }

    if (
      !travelSearch.selectedJourneyTo?.PlaceName.length ||
      travelSearch.selectedJourneyTo.PlaceName.length < 1
    ) {
      errors.journeyToError = "Vänligen fyll i en destination";
    }

    if (!travelSearch.journeyDate.length) {
      errors.journeyDateError = "Vänligen fyll i ett datum";
    } else {
      const today = dayjs().startOf("day");
      const minDate = today.add(5, "days").startOf("day");
      const selectedDate = dayjs(travelSearch.journeyDate);

      if (selectedDate.isBefore(minDate)) {
        errors.journeyDateError =
          "Du kan endast välja ett datum som är minst 5 dagar framåt";
      }
    }

    if (travelSearch.journeyTime.length < 1) {
      errors.journeyTimeError = "Vänligen fyll i en tid";
    }

    setSearchErrors((prevErrors) => ({
      ...prevErrors,
      ...errors,
    }));

    const hasErrors = Object.values(errors).some(
      (error) => error.trim().length > 0
    );

    if (hasErrors) {
      setIsSearching(false);
      setSearchResults([]);
      setFormData((prev) => ({
        ...prev,
        journey: "",
      }));
      return;
    }

    if (clearSelectedJourney) {
      setSelectedJourney({
        fromName: "",
        toName: "",
        date: "",
        travelTime: "",
        journeyKey: "",
        line: [],
        switches: "",
        unformattedDate: "",
      });

      setFormData((prev) => ({
        ...prev,
        journey: "",
        //selectedJourney,
      }));
    }

    const params: FindGroupJourneysParams = {
      startId: travelSearch.selectedJourneyFrom!.Id,
      endId: travelSearch.selectedJourneyTo!.Id,
      startType: travelSearch.selectedJourneyFrom!.OgtType,
      endType: travelSearch.selectedJourneyTo!.OgtType,
      startLl: `${travelSearch.selectedJourneyFrom!.Ll[0]},${
        travelSearch.selectedJourneyFrom!.Ll[1]
      }`,
      endLl: `${travelSearch.selectedJourneyTo!.Ll[0]},${
        travelSearch.selectedJourneyTo!.Ll[1]
      }`,
      startName: travelSearch.selectedJourneyFrom!.PlaceName,
      endName: travelSearch.selectedJourneyTo!.PlaceName,
      date: travelSearch.journeyDate,
      time: formatTimeInput(travelSearch.journeyTime),
      direction: 0,
      span: "Default",
      trafficTypes: 0,
      changetime: "Normal",
      priority: "ShortestTime",
      walk: false,
    };

    try {
      const data = await findGroupJourneys(params);

      const journeys = data.Journeys.map((journey) => ({
        ...journey,
        BookedGroupJourneys: journey.BookedGroupJourneys ?? 0,
      }));

      setSearchResults(journeys);

      let errorText = data.ErrorText || "";

      if (errorText.includes("JourneyDateTime")) {
        errorText = "Vänligen ange en giltig tid";
      }

      setSearchErrors({
        journeyFromError: "",
        journeyToError: "",
        journeyTimeError: "",
        journeyDateError: "",
        journeyFormError: errorText,
        formError: "",
      });
    } catch (error) {
      console.error("[Journey search]", error);

      setSearchResults([]);

      setSearchErrors({
        journeyFromError: "",
        journeyToError: "",
        journeyTimeError: "",
        journeyDateError: "",
        journeyFormError:
          error instanceof Error
            ? error.message
            : "Det gick inte att hämta resor. Försök igen senare.",
        formError: "",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const formatSelectedJourney = (journey: any) => {
    const fromPlaceId = journey.UsedStartPlace?.Id;
    const toPlaceId = journey.UsedEndPlace?.Id;
    const fromOgtType = journey.UsedStartPlace?.OgtType;
    const toOgtType = journey.UsedEndPlace?.OgtType;

    const lineWithPlaces = (journey.RouteLinks || []).map(
      (link: any, index: number, arr: any[]) => ({
        ...link,
        ...(index === 0
          ? {
              From: {
                ...link.From,
                Id: fromPlaceId,
                OgtType: fromOgtType,
              },
            }
          : {}),
        ...(index === arr.length - 1
          ? {
              To: {
                ...link.To,
                Id: toPlaceId,
                OgtType: toOgtType,
              },
            }
          : {}),
      })
    );

    return {
      fromName: journey.UsedStartPlace.PlaceName,
      toName: journey.UsedEndPlace.PlaceName,
      date: formatDate(journey.Departure, "dddd D MMMM"),
      travelTime: `${formatDate(journey.Departure, "HH:mm")} - ${formatDate(
        journey.Arrival,
        "HH:mm"
      )}`,
      journeyKey: journey.JourneyKey,
      line: lineWithPlaces,
      switches: journey.Switch,
      unformattedDate: journey.Departure,
      unformattedArrival: journey.Arrival,
      UsedStartPlace: journey.UsedStartPlace,
      UsedEndPlace: journey.UsedEndPlace,
    };
  };

  const formatDate = (date: string, format: string) => {
    const formattedDate = dayjs(date).locale("sv").format(format);
    return formattedDate;
  };

  const groupResultsByDate = (results: Journeys[]) => {
    return results.reduce((acc: Record<string, Journeys[]>, result) => {
      const date = formatDate(result.Departure, "dddd D MMMM");
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(result);
      return acc;
    }, {});
  };

  const updateFormData = (_value: string, result: Journeys) => {
    setSelectedJourney((currentJourney) => {
      if (currentJourney.journeyKey === result.JourneyKey) {
        return currentJourney;
      }

      return formatSelectedJourney(result);
    });
  };

  const validationSchema = Yup.object().shape({
    journey: Yup.string()
      .test(
        "at-least-one-radio-selected",
        "Vänligen välj en resa för att gå vidare till nästa steg",
        (value) => {
          return searchResults.some((result) => result.JourneyKey === value);
        }
      )
      .test(
        "not-fully-booked",
        "Resan är fullbokad, vänligen välj en annan resa",
        function (value) {
          const selectedJourney = searchResults.find(
            (journey) => journey.JourneyKey === value
          );

          if (!selectedJourney) return false; // No journey selected

          return (selectedJourney.BookedGroupJourneys ?? 0) === 0;
        }
      ),
  });

  return (
    <div className="travel-search-wrapper">
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
            selectedJourney,
            ...(values.traveltype === ONEWAY_TRIP && {
              selectedReturnJourney: {
                fromName: "",
                toName: "",
                date: "",
                travelTime: "",
                journeyKey: "",
                line: [],
                switches: 0,
                unformattedDate: "",
              },
            }),
          });

          if (values.traveltype === ROUND_TRIP) {
            navigate("/valj-aterresa");
          } else {
            navigate("/er-resa");

            // Reset return trip data
            setFormData((prev) => ({
              ...prev,
              selectedReturnJourney: {
                fromName: "",
                toName: "",
                date: "",
                travelTime: "",
                journeyKey: "",
                line: [],
                switches: 0,
                unformattedDate: "",
              },
            }));
          }
        }}
        initialValues={{
          ...formData,
          journey: formData.selectedJourney?.journeyKey || "",
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
      >
        {({ errors, values }) => {
          const politeAnnouncement = !hasSearched
            ? ""
            : isSearching
            ? "Söker resor."
            : searchErrors.journeyFormError
            ? ""
            : searchResults.length === 0
            ? "Inga resor hittades."
            : `${searchResults.length} ${
                searchResults.length === 1 ? "resa hittades" : "resor hittades"
              }. Välj en resa i listan nedan.`;

          const assertiveAnnouncement =
            searchErrors.journeyFormError ||
            searchErrors.journeyFromError ||
            searchErrors.journeyToError ||
            searchErrors.journeyDateError ||
            searchErrors.journeyTimeError ||
            (errors?.journey ? String(errors.journey) : "");

          const combinedPoliteMessage = [
            politeAnnouncement,
            autocompleteAnnouncement,
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <>
              <ScreenReaderAnnouncer
                politeMessage={combinedPoliteMessage}
                assertiveMessage={assertiveAnnouncement}
              />

              <div className="flex justify-end items-center">
                <div className="w-full max-w-xs mb-4">
                  <Progressbar percentage={progress} />
                </div>
              </div>

              <Form ref={formRef} noValidate>
                <JourneySelectionWatcher
                  searchResults={searchResults}
                  onSelect={updateFormData}
                />

                <Box fullwidth={true}>
                  <Heading
                    progress={progress}
                    headingRef={h2Ref}
                    focusable
                    id="travelSearchHeading"
                  >
                    Vart vill ni resa?
                  </Heading>

                  <p>
                    Välj först om det är en tur och retur- eller enkelresa.
                    Reser ni tur och retur fyller ni först i resan dit. Resan
                    tillbaka tar vi i nästa steg.
                  </p>
                  <p>
                    Tänk på att ni måste anmäla er gruppresa minst fem dagar i
                    förväg.
                  </p>

                  <Heading as="h3" id="WeWillTravel" className="mb-2">
                    Vi kommer att resa
                  </Heading>

                  <RadioGroupForm
                    name="traveltype"
                    ariaLabelledBy="WeWillTravel"
                    orientation="horizontal"
                    variant="default"
                    className="mt-2 mb-6"
                    defaultChecked={ROUND_TRIP}
                    options={[
                      {
                        id: "roundTrip",
                        value: ROUND_TRIP,
                        label: "Tur och retur",
                      },
                      {
                        id: "oneWayTrip",
                        value: ONEWAY_TRIP,
                        label: "Enkelresa",
                      },
                    ]}
                  />

                  <Heading as="h3" className="mb-2">
                    Resa till
                  </Heading>

                  {searchErrors.journeyFormError.length > 0 && (
                    <Alert showIcon className="mb-2" variant="error">
                      <AlertTitle>{searchErrors.journeyFormError}</AlertTitle>
                    </Alert>
                  )}

                  <div
                    className="travel-search-form grid gap-6 grid-cols-1 md:grid-cols-2"
                    ref={searchFormRef}
                  >
                    <CustomAutocomplete
                      id="journeyFrom"
                      label="Från"
                      errorMessage={searchErrors.journeyFromError}
                      value={
                        fromQuery !== undefined
                          ? fromQuery
                          : travelSearch.selectedJourneyFrom?.PlaceName || ""
                      }
                      onChange={(e: any) => {
                        setFromQuery(e.target.value);
                        setSearchType("from");
                      }}
                      onQueryChange={handleQueryChange}
                      options={fromResults}
                      onSelect={(placeName: any) =>
                        handleSelect(placeName, "from")
                      }
                      type="from"
                      onKeyDown={handleKeyDown}
                    />

                    <CustomAutocomplete
                      id="journeyTo"
                      label="Till"
                      errorMessage={searchErrors.journeyToError}
                      value={
                        toQuery !== undefined
                          ? toQuery
                          : travelSearch.selectedJourneyTo?.PlaceName || ""
                      }
                      onChange={(e: any) => {
                        setToQuery(e.target.value);
                        setSearchType("to");
                      }}
                      onQueryChange={handleQueryChange}
                      options={toResults}
                      onSelect={(placeName: any) =>
                        handleSelect(placeName, "to")
                      }
                      type="to"
                      onKeyDown={handleKeyDown}
                    />

                    <DatePicker
                      name="journey_date"
                      label={
                        <>
                          <span>Datum för resa</span>{" "}
                          <span>(Obligatoriskt)</span>
                        </>
                      }
                      value={travelSearch.journeyDate}
                      onChange={(value: string) =>
                        handleTravelSearchChange("journeyDate", value)
                      }
                      error={searchErrors.journeyDateError}
                      maxDate={new Date().toISOString().split("T")[0]}
                      onKeyDown={handleKeyDown}
                      enableFromFiveDaysAhead
                    />

                    <div className="input-container gap-2 flex flex-col">
                      <Label htmlFor="journey_time">
                        <span>
                          Ungefärlig avgångstid <span>(Obligatoriskt)</span>
                        </span>
                      </Label>

                      <Input
                        type="text"
                        id="journey_time"
                        name="journey_time"
                        value={travelSearch.journeyTime || ""}
                        onChange={(e) =>
                          handleTravelSearchChange(
                            "journeyTime",
                            e.target.value
                          )
                        }
                        onKeyDown={handleKeyDown}
                        className={`${
                          searchErrors.journeyTimeError ? "has-error" : ""
                        } `}
                      />

                      {searchErrors.journeyTimeError.length > 0 && (
                        <Alert showIcon variant="error">
                          <AlertTitle>
                            {searchErrors.journeyTimeError}
                          </AlertTitle>
                        </Alert>
                      )}
                    </div>
                  </div>

                  <Button
                    iconPosition="right"
                    aria-label="Sök resa"
                    className="mt-6 w-full sm:w-auto "
                    size="medium"
                    variant="primary"
                    onClick={() => doTravelSearch(true)}
                    id="searchButton"
                    ref={searchButtonRef}
                  >
                    Sök resa
                  </Button>

                  {Object.keys(errors).length > 0 && (
                    <div className="mt-4">
                      <ErrorList errors={errors} />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="search-results-container mt-6 flex flex-col gap-6">
                      {Object.entries(groupResultsByDate(searchResults)).map(
                        ([date, results]) => (
                          <div key={date}>
                            {errors.journey && (
                              <Alert showIcon variant="error">
                                <AlertTitle>
                                  {errors.journey.toString()}
                                </AlertTitle>
                              </Alert>
                            )}

                            {(() => {
                              const uniqueResults = results.filter(
                                (item, index, self) =>
                                  index ===
                                  self.findIndex(
                                    (t) => t.JourneyKey === item.JourneyKey
                                  )
                              );

                              const options = uniqueResults.map((result) => {
                                const isChecked =
                                  values.journey === result.JourneyKey;
                                const isBooked =
                                  result.BookedGroupJourneys === 1;

                                const Lines =
                                  result.RouteLinks &&
                                  Array.isArray(result.RouteLinks) ? (
                                    <span className="lineicon-container flex-wrap inline-flex items-center gap-1">
                                      {result.RouteLinks.map((item, index) => {
                                        const meta = toTrafficMeta(item);
                                        const isPendeln =
                                          item.Line.LineName?.includes(
                                            "Östgötapendeln"
                                          );
                                        const showText =
                                          !item.Line.Ogt
                                            .OgtTrafficTypeIconReplaceLineName;

                                        return (
                                          <TrafficBadge
                                            key={index}
                                            meta={meta}
                                            className={cn(
                                              "lineicon",
                                              item.Line.LineTypeName,
                                              item.Line.Ogt
                                                .OgtTrafficTypeGroupCssClass,
                                              isPendeln && "pendeln"
                                            )}
                                            titleOverride={undefined}
                                          >
                                            {showText
                                              ? item.Line.LineName
                                              : null}
                                          </TrafficBadge>
                                        );
                                      })}
                                    </span>
                                  ) : null;

                                return {
                                  id: result.JourneyKey,
                                  value: result.JourneyKey,
                                  disabled: isBooked,
                                  label: (
                                    <>
                                      <span className="sr-only">
                                        {getJourneySrLabel(result, formatDate, {
                                          isBooked: isBooked,
                                        })}
                                      </span>

                                      <div
                                        aria-hidden="true"
                                        className={[
                                          "flex flex-row w-full gap-x-6 gap-y-2 items-center flex-wrap [&>*]:leading-normal",
                                          isChecked ? "checked" : "",
                                          isBooked ? "fully-booked" : "",
                                        ]
                                          .filter(Boolean)
                                          .join(" ")}
                                      >
                                        {Lines}

                                        <span className="group-hover/radio-row:underline travel-departure-arrival inline-flex items-center">
                                          {formatDate(
                                            result.Departure,
                                            "HH:mm"
                                          )}{" "}
                                          –{" "}
                                          {formatDate(result.Arrival, "HH:mm")}
                                        </span>

                                        <span className="travel-time inline-flex items-center md:ml-auto min-w-[100px]">
                                          Restid: {result.TravelTime}
                                        </span>

                                        <span className="switch inline-flex items-center min-w-[60px]">
                                          {result.Switch}{" "}
                                          {parseInt(result.Switch, 10) === 1
                                            ? "byte"
                                            : "byten"}
                                        </span>

                                        {isBooked && (
                                          <Badge
                                            variant="red"
                                            shape="rounded"
                                            state="default"
                                            icons={
                                              <Icon
                                                name="alert-triangle"
                                                size={14}
                                              />
                                            }
                                          >
                                            Fullbokad
                                          </Badge>
                                        )}
                                      </div>
                                    </>
                                  ),
                                };
                              });

                              return (
                                <>
                                  <Heading as="h3">
                                    {date}, {uniqueResults.length}{" "}
                                    {uniqueResults.length > 1
                                      ? "resor"
                                      : "resa"}
                                  </Heading>

                                  <p className="full-width">
                                    Sök resan i appen eller på webben för att se
                                    mer information om de olika avgångarna.
                                  </p>

                                  <RadioGroupForm
                                    name="journey"
                                    legend="Välj en resa"
                                    label={null}
                                    description={null}
                                    orientation="vertical"
                                    variant="box"
                                    required
                                    underlineOnHover={false}
                                    className={cn(
                                      "radio-group !w-full mb-6",
                                      errors.journey && "has-error"
                                    )}
                                    itemClassName=""
                                    options={options}
                                  />
                                </>
                              );
                            })()}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </Box>

                <div className="flex justify-center">
                  <div className="w-full text-left flex items-center">
                    <Button
                      iconPosition="left"
                      iconSize={16}
                      iconName="arrow-left"
                      aria-label="Tillbaka"
                      showIcon
                      size="medium"
                      variant="outline"
                      onClick={(event) => {
                        event.preventDefault();
                        const backUrl = undefined;
                        if (backUrl) {
                          navigate(backUrl, { replace: true });
                        } else {
                          navigate(-1);
                        }
                      }}
                    >
                      Tillbaka
                    </Button>
                  </div>

                  <div className="w-full text-right">
                    {searchResults.length > 0 && (
                      <Button
                        type="submit"
                        className="btn primary"
                        disabled={isFocused}
                      >
                        Gå vidare
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}
